import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  PanOnScrollMode,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  useStore,
  useReadOnly,
  selectActiveNodes,
  selectActiveEdges,
  activeSheet,
  isCompNode,
  isPowerSymbolNode,
  type AnyNode,
  type WireEdge,
} from '../store';
import { isPowerNetName } from '../nets';
import type { InterconnectBundle } from '../types';
import { ComponentNode } from './ComponentNode';
import { AdjustableEdge } from './AdjustableEdge';
import { AnnotationNode } from './AnnotationNode';
import { ImageNode } from './ImageNode';
import { BlockNode } from './BlockNode';
import { MechanicalItemNode } from './MechanicalItemNode';
import { MeasurementNode } from './MeasurementNode';
import { MechanicalLegend } from './MechanicalLegend';
import { MechanicalSelectionInfo } from './MechanicalSelectionInfo';
import { PowerSymbolNode } from './PowerSymbolNode';
import { NetLabelNode } from './NetLabelNode';
import { SpliceNode } from './SpliceNode';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';
import { NameModal } from './SheetModals';
import { RequirementsSheet } from './RequirementsSheet';
import { InterconnectSheet } from './InterconnectSheet';
import { DocumentSheet } from './DocumentSheet';
import { fitImageSize, readClipboardImage } from '../clipboardImages';
import { requirePro } from '../entitlements';

const nodeTypes = {
  component: ComponentNode,
  annotation: AnnotationNode,
  image: ImageNode,
  block: BlockNode,
  mechanicalItem: MechanicalItemNode,
  measurement: MeasurementNode,
  powerSymbol: PowerSymbolNode,
  netLabel: NetLabelNode,
  splice: SpliceNode,
};
const edgeTypes = { adjustable: AdjustableEdge };

function getCanvasNodeLabel(node: AnyNode | undefined) {
  if (!node) return '';
  const data = node.data as { label?: unknown; name?: unknown; title?: unknown };
  const value = data.label ?? data.name ?? data.title;
  return typeof value === 'string' && value.trim() ? value.trim() : node.type;
}

function CanvasInner() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition, fitView } = useReactFlow<AnyNode, WireEdge>();
  const [imagePasteBusy, setImagePasteBusy] = useState(false);

  const nodes = useStore(selectActiveNodes);
  const edgesRaw = useStore(selectActiveEdges);
  const blockDiagramSimplified = useStore((s) => s.blockDiagramSimplified);
  const activeSheetKind = useStore((s) => activeSheet(s).kind);

  // "Simplified" block-diagram view: when active on a block sheet, we
  // collapse multiple harness-edges between the same pair of blocks into
  // a single visual edge with an aggregate label ("MOTOR_HARN · HALL_HARN"
  // or "3 harnesses"). Pure render-time transform — store data unchanged.
  const edges = useMemo(() => {
    if (!blockDiagramSimplified || activeSheetKind !== 'block') return edgesRaw;
    const groups = new Map<string, WireEdge[]>();
    for (const e of edgesRaw) {
      if (!e.source || !e.target) {
        groups.set(e.id, [e]);
        continue;
      }
      const key =
        e.source < e.target ? `${e.source}|${e.target}` : `${e.target}|${e.source}`;
      const list = groups.get(key);
      if (list) list.push(e);
      else groups.set(key, [e]);
    }
    const out: WireEdge[] = [];
    for (const list of groups.values()) {
      if (list.length === 1) {
        out.push(list[0]);
      } else {
        // Use the first edge as the visual template; tag with merged ids
        // so AdjustableEdge can paint the aggregate label.
        const first = list[0];
        const harnessIds = list
          .map((e) => e.data?.harnessId)
          .filter((v): v is string => Boolean(v));
        out.push({
          ...first,
          data: {
            ...first.data,
            mergedHarnessIds: harnessIds,
            mergedCount: list.length,
          },
        });
      }
    }
    return out;
  }, [edgesRaw, blockDiagramSimplified, activeSheetKind]);

  // Power-wire filter (ribbon Tools → Power filter). Power-ness is detected
  // from BOTH the connected pin kind (power/gnd) AND the wire's net name or
  // label (12V / GND / VMOT…) — real projects carry power-ness in pin kinds
  // and labels, not only in an explicit `netName`. Hide via `edge.hidden` so
  // it works uniformly on block + schematic sheets.
  const wireFilter = useStore((s) => s.wireFilter);
  const edgesForRender = useMemo(() => {
    if (wireFilter === 'all') return edges;
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const endpointIsPower = (nodeId: string | null | undefined, handle?: string | null) => {
      if (!nodeId) return false;
      const node = nodeById.get(nodeId);
      if (!node) return false;
      if (isPowerSymbolNode(node)) return true;
      if (isCompNode(node)) {
        const pin = node.data.pins.find((p) => p.id === handle);
        if (pin && (pin.kind === 'power' || pin.kind === 'gnd')) return true;
      }
      return false;
    };
    const isPowerWire = (e: WireEdge) =>
      isPowerNetName(e.data?.netName) ||
      isPowerNetName(typeof e.data?.label === 'string' ? e.data.label : undefined) ||
      endpointIsPower(e.source, e.sourceHandle) ||
      endpointIsPower(e.target, e.targetHandle);
    return edges.map((e) => {
      const power = isPowerWire(e);
      const hidden = wireFilter === 'no-power' ? power : !power;
      return hidden === Boolean(e.hidden) ? e : { ...e, hidden };
    });
  }, [edges, nodes, wireFilter]);

  const onNodesChange = useStore((s) => s.onNodesChange);
  const onEdgesChange = useStore((s) => s.onEdgesChange);
  const onConnect = useStore((s) => s.onConnect);
  const onReconnect = useStore((s) => s.onReconnect);
  const addComponent = useStore((s) => s.addComponent);
  const addAnnotation = useStore((s) => s.addAnnotation);
  const addMeasurement = useStore((s) => s.addMeasurement);
  const addImage = useStore((s) => s.addImage);
  const readOnly = useReadOnly();
  const selectNode = useStore((s) => s.selectNode);
  const selectEdge = useStore((s) => s.selectEdge);
  const rotateNode = useStore((s) => s.rotateNode);
  const deleteNode = useStore((s) => s.deleteNode);
  const deleteEdge = useStore((s) => s.deleteEdge);
  const updateEdgeData = useStore((s) => s.updateEdgeData);
  const copySelectedNode = useStore((s) => s.copySelectedNode);
  const pasteClipboard = useStore((s) => s.pasteClipboard);
  const setLastPastePosition = useStore((s) => s.setLastPastePosition);
  const pendingTool = useStore((s) => s.pendingTool);
  const setPendingTool = useStore((s) => s.setPendingTool);
  const snapToGrid = useStore((s) => s.snapToGrid);
  const focusedHarnessId = useStore((s) => s.focusedHarnessId);
  const focusHarness = useStore((s) => s.focusHarness);
  const focusedNet = useStore((s) => s.focusedNet);
  const focusedNetMode = useStore((s) => s.focusedNetMode);
  const [edgeMenu, setEdgeMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);
  const [measureStart, setMeasureStart] = useState<{ x: number; y: number } | null>(null);
  // Net-name dialog (React modal — window.prompt does not work in Electron).
  const [netDialog, setNetDialog] = useState<{ edgeId: string; initial: string } | null>(null);
  // Look up the bundle on demand WITHOUT subscribing — `selectAllBundles`
  // builds a new array on every call which would be a churn-y subscription
  // that re-renders this component on every state change. We only need the
  // bundle name when focus is active, so just peek at the store imperatively.
  const focusedBundle: InterconnectBundle | null = useMemo(() => {
    if (!focusedHarnessId) return null;
    for (const sh of useStore.getState().sheets) {
      if (sh.kind === 'interconnect' && sh.bundles) {
        const b = sh.bundles.find((bb) => bb.id === focusedHarnessId);
        if (b) return b;
      }
    }
    return null;
  }, [focusedHarnessId]);

  // Track last mouse position in flow coords so paste can land under the cursor.
  const lastPointerFlow = useRef<{ x: number; y: number } | null>(null);

  const pasteSmart = useCallback(
    async (position?: { x: number; y: number }) => {
      const anchor = position ?? lastPointerFlow.current ?? undefined;
      setImagePasteBusy(true);
      const image = await readClipboardImage();
      setImagePasteBusy(false);
      if (image) {
        if (!requirePro('Adding photos')) return true;
        const size = fitImageSize(image);
        addImage(anchor ?? { x: 80, y: 80 }, image.src, size, image.name);
        return true;
      }
      return pasteClipboard(anchor);
    },
    [addImage, pasteClipboard],
  );

  const defaultNetNameForEdge = useCallback((edge: WireEdge) => {
    const explicit = typeof edge.data?.netName === 'string' ? edge.data.netName.trim() : '';
    if (explicit) return explicit;
    const label = typeof edge.data?.label === 'string' ? edge.data.label.trim() : '';
    if (label) return label;
    const source = nodes.find((node) => node.id === edge.source);
    const target = nodes.find((node) => node.id === edge.target);
    return `${getCanvasNodeLabel(source)}_${getCanvasNodeLabel(target)}`
      .replace(/[^a-z0-9_+-]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase() || 'NET';
  }, [nodes]);

  const openEdgeMenu = useCallback(
    (event: ReactMouseEvent, edge: WireEdge) => {
      event.preventDefault();
      event.stopPropagation();
      selectEdge(edge.id);
      const currentName = typeof edge.data?.netName === 'string' ? edge.data.netName : '';
      const initial = currentName || defaultNetNameForEdge(edge);
      const items: ContextMenuItem[] = [
        {
          label: 'Assign net name…',
          onClick: () => setNetDialog({ edgeId: edge.id, initial }),
        },
        { label: '', separator: true },
        {
          label: 'Clear net name',
          disabled: !currentName,
          onClick: () => updateEdgeData(edge.id, { netName: undefined }),
        },
      ];
      setEdgeMenu({ x: event.clientX, y: event.clientY, items });
    },
    [defaultNetNameForEdge, selectEdge, updateEdgeData],
  );

  const nodesForRender = useMemo(() => {
    if (!focusedNet) return nodes;
    const focusedIds = new Set(focusedNet.nodeIds);
    return nodes.map((node) => {
      const inNet = focusedIds.has(node.id);
      return {
        ...node,
        style: {
          ...(node.style ?? {}),
          opacity: inNet ? 1 : focusedNetMode === 'solo' ? 0.2 : 0.35,
          filter: inNet ? 'drop-shadow(0 0 8px rgba(47,129,247,0.62))' : undefined,
        },
      };
    });
  }, [focusedNet, focusedNetMode, nodes]);

  // When a harness gets focused, zoom the canvas to the nodes those wires
  // touch — otherwise the user lands on the schematic still seeing the
  // whole sheet and has to manually find the highlighted run. We wait one
  // animation frame so React Flow has the new edges/nodes laid out.
  useEffect(() => {
    if (!focusedHarnessId) return;
    const focusNodeIds = new Set<string>();
    for (const e of edges) {
      if (e.data?.harnessId !== focusedHarnessId) continue;
      if (e.source) focusNodeIds.add(e.source);
      if (e.target) focusNodeIds.add(e.target);
    }
    if (focusNodeIds.size === 0) return;
    const nodeRefs = Array.from(focusNodeIds).map((id) => ({ id }));
    // Two RAFs gives React Flow enough time to mount + measure node sizes
    // (it skips fitView on nodes whose size hasn't been read yet).
    const t = setTimeout(() => {
      fitView({
        nodes: nodeRefs,
        padding: 0.25,
        duration: 450,
        maxZoom: 1.4,
        minZoom: 0.2,
      });
    }, 80);
    return () => clearTimeout(t);
  }, [focusedHarnessId, edges, fitView]);

  // Focus / zoom the viewport onto specific nodes (or an edge's endpoints) on
  // request — used by the Rule Checker to jump to an issue. Fired via a window
  // event so callers don't need the React Flow instance. Re-registers on every
  // sheet swap (Canvas re-mounts), so a delayed dispatch lands on the right one.
  useEffect(() => {
    const onFocus = (ev: Event) => {
      const detail = (ev as CustomEvent<{ nodeIds?: string[]; edgeId?: string }>).detail ?? {};
      const ids = new Set<string>(detail.nodeIds ?? []);
      if (detail.edgeId) {
        const e = edges.find((x) => x.id === detail.edgeId);
        if (e?.source) ids.add(e.source);
        if (e?.target) ids.add(e.target);
      }
      if (ids.size === 0) return;
      const refs = Array.from(ids).map((id) => ({ id }));
      // Frame the target node(s) — mirrors the harness-focus fitView path.
      setTimeout(() => {
        fitView({ nodes: refs, padding: 0.4, duration: 450, maxZoom: 1.3, minZoom: 0.2 });
      }, 60);
    };
    window.addEventListener('skema:focus', onFocus as EventListener);
    return () => window.removeEventListener('skema:focus', onFocus as EventListener);
  }, [edges, fitView]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }

      const mod = e.ctrlKey || e.metaKey;
      const isUndo = mod && !e.shiftKey && (e.key === 'z' || e.key === 'Z');
      const isRedo =
        mod && ((e.shiftKey && (e.key === 'z' || e.key === 'Z')) || e.key === 'y' || e.key === 'Y');
      const isCopy = mod && (e.key === 'c' || e.key === 'C');
      const isPaste = mod && (e.key === 'v' || e.key === 'V');
      const isDuplicate = mod && (e.key === 'd' || e.key === 'D');

      if (isUndo) {
        e.preventDefault();
        useStore.temporal.getState().undo();
        return;
      }
      if (isRedo) {
        e.preventDefault();
        useStore.temporal.getState().redo();
        return;
      }
      if (isCopy) {
        if (copySelectedNode()) e.preventDefault();
        return;
      }
      if (isPaste) {
        const pos = lastPointerFlow.current ?? undefined;
        e.preventDefault();
        void pasteSmart(pos);
        return;
      }
      if (isDuplicate) {
        // Ctrl+D: copy current selection and paste immediately (offset).
        if (copySelectedNode() && pasteClipboard()) e.preventDefault();
        return;
      }

      const state = useStore.getState();
      if (e.key === 'Escape' && state.pendingTool) {
        e.preventDefault();
        useStore.getState().setPendingTool(null);
        setMeasureStart(null);
        return;
      }
      if (e.key === 'Escape' && state.focusedHarnessId) {
        e.preventDefault();
        useStore.getState().focusHarness(null);
        return;
      }
      if (e.key === 'Escape' && state.focusedNet) {
        e.preventDefault();
        useStore.getState().focusNet(null);
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        const id = state.selectedNodeId;
        if (id) {
          e.preventDefault();
          rotateNode(id, e.shiftKey ? -90 : 90);
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        const sheet = activeSheet(state);
        const selectedNodes = sheet.nodes.filter((n) => n.selected);
        const selectedEdges = sheet.edges.filter((ed) => ed.selected);
        const primaryNode =
          state.selectedNodeId && !selectedNodes.length ? [state.selectedNodeId] : selectedNodes.map((n) => n.id);
        const primaryEdge =
          state.selectedEdgeId && !selectedEdges.length ? [state.selectedEdgeId] : selectedEdges.map((ed) => ed.id);
        if (primaryNode.length || primaryEdge.length) {
          e.preventDefault();
          for (const id of primaryNode) deleteNode(id);
          for (const id of primaryEdge) deleteEdge(id);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [rotateNode, deleteNode, deleteEdge, copySelectedNode, pasteClipboard, pasteSmart]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const key = e.dataTransfer.getData('application/skema-component');
      if (!key) return;
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      addComponent(key, position);
    },
    [addComponent, screenToFlowPosition],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      lastPointerFlow.current = pos;
      setLastPastePosition(pos);
    },
    [screenToFlowPosition, setLastPastePosition],
  );

  return (
    <div
      ref={wrapperRef}
      onPointerMove={onPointerMove}
      style={{
        flex: 1,
        height: '100%',
        background: 'var(--paper)',
        position: 'relative',
        cursor: pendingTool === 'annotation' || pendingTool === 'measure' ? 'crosshair' : undefined,
      }}
    >
      {activeSheetKind === 'mechanical' && <MechanicalLegend />}
      {activeSheetKind === 'mechanical' && <MechanicalSelectionInfo />}
      {pendingTool === 'annotation' && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
            color: 'var(--text-muted)',
            fontSize: 11,
            boxShadow: 'var(--shadow)',
            pointerEvents: 'none',
          }}
        >
          Click canvas to place note
        </div>
      )}
      {pendingTool === 'measure' && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
            color: 'var(--text-muted)',
            fontSize: 11,
            boxShadow: 'var(--shadow)',
            pointerEvents: 'none',
          }}
        >
          {measureStart ? 'Click second point to finish measurement' : 'Click first measurement point'}
        </div>
      )}
      {imagePasteBusy && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: pendingTool === 'annotation' ? 176 : 12,
            zIndex: 10,
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
            color: 'var(--text-muted)',
            fontSize: 11,
            boxShadow: 'var(--shadow)',
            pointerEvents: 'none',
          }}
        >
          Reading clipboard image...
        </div>
      )}
      {focusedBundle && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0,
            background: 'var(--panel)',
            border: '1px solid var(--panel-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-lg, 0 6px 16px rgba(0,0,0,0.18))',
            overflow: 'hidden',
            fontSize: 11,
            color: 'var(--text)',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderLeft: '3px solid var(--accent)',
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              Harness focus
            </span>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>
              {focusedBundle.name || '(unnamed)'}
            </span>
          </span>
          <button
            onClick={() => focusHarness(null)}
            title="Exit harness focus mode (Esc)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 10px',
              background: 'transparent',
              color: 'var(--text-muted)',
              border: 'none',
              borderLeft: '1px solid var(--panel-border)',
              cursor: 'pointer',
              fontSize: 11,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--panel-alt)';
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            <span style={{ fontSize: 13, lineHeight: 1 }}>×</span>
            <span style={{ fontSize: 10 }}>Exit</span>
          </button>
        </div>
      )}

      <ReactFlow<AnyNode, WireEdge>
        nodes={nodesForRender}
        edges={edgesForRender}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        edgesReconnectable={!readOnly}
        deleteKeyCode={readOnly ? null : undefined}
        reconnectRadius={14}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={(_, n) => selectNode(n.id)}
        onEdgeClick={(_, ed) => selectEdge(ed.id)}
        onEdgeContextMenu={(event, edge) => openEdgeMenu(event, edge)}
        onPaneClick={(e) => {
          setEdgeMenu(null);
          if (pendingTool === 'annotation') {
            const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
            addAnnotation(position, '');
            setPendingTool(null);
            return;
          }
          if (pendingTool === 'measure') {
            const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
            if (!measureStart) {
              setMeasureStart(position);
              return;
            }
            addMeasurement(measureStart, position);
            setMeasureStart(null);
            setPendingTool(null);
            return;
          }
          selectNode(null);
          selectEdge(null);
        }}
        fitView
        minZoom={0.02}
        maxZoom={4}
        panOnScroll
        panOnScrollMode={PanOnScrollMode.Free}
        panOnScrollSpeed={0.9}
        zoomOnScroll={false}
        zoomOnPinch
        snapToGrid={snapToGrid}
        snapGrid={[20, 20]}
        selectionOnDrag
        elevateNodesOnSelect={false}
        panOnDrag={[1, 2]}
        multiSelectionKeyCode={['Shift', 'Meta', 'Control']}
        defaultEdgeOptions={{
          type: 'adjustable',
          animated: false,
          style: { stroke: '#64748b', strokeWidth: 1.5 },
        }}
      >
        <Background
          color="var(--paper-grid)"
          gap={22}
          size={1.35}
        />
        <MiniMap
          nodeColor={(n) => (n.data as { color?: string }).color ?? '#94a3b8'}
          maskColor="rgba(226, 232, 240, 0.7)"
          style={{ background: '#ffffff' }}
        />
        <Controls />
      </ReactFlow>
      {edgeMenu && (
        <ContextMenu
          x={edgeMenu.x}
          y={edgeMenu.y}
          items={edgeMenu.items}
          onClose={() => setEdgeMenu(null)}
        />
      )}
      {netDialog && (
        <NameModal
          title="Assign net name"
          defaultValue={netDialog.initial}
          okLabel="Assign"
          onCancel={() => setNetDialog(null)}
          onSubmit={(name) => {
            const n = name.trim();
            if (n) updateEdgeData(netDialog.edgeId, { netName: n });
            setNetDialog(null);
          }}
        />
      )}
    </div>
  );
}

function CanvasRouter() {
  const sheetCount = useStore((s) => s.sheets.length);
  const sheetKind = useStore((s) => activeSheet(s).kind);
  // Re-key by active sheet id so ReactFlow tears down and re-mounts cleanly
  // when the user switches sheets (or restores a project), instead of trying
  // to reconcile the previous viewport / fitView against the new graph.
  const activeSheetId = useStore((s) => s.activeSheetId);
  // Brand-new project state — no sheets at all. Render a soft empty area;
  // the WelcomeBanner overlays a clear call-to-action on top of this.
  if (sheetCount === 0) return <EmptyProjectCanvas />;
  if (sheetKind === 'requirements') return <RequirementsSheet />;
  if (sheetKind === 'interconnect') return <InterconnectSheet />;
  if (sheetKind === 'document') return <DocumentSheet />;
  return <CanvasInner key={activeSheetId} />;
}

function EmptyProjectCanvas() {
  return (
    <div
      style={{
        flex: 1,
        height: '100%',
        background: 'var(--bg-canvas)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-muted)',
        fontSize: 12,
        fontStyle: 'italic',
      }}
    >
      Project is empty — add a subsystem or sheet from the project tree to
      start.
    </div>
  );
}

export function Canvas() {
  return (
    <ReactFlowProvider>
      <CanvasRouter />
    </ReactFlowProvider>
  );
}
